/**
 * #2496 — where the CP auth server sends an in-domain person who has no operator account yet.
 *
 * ⛔ THE COPY ASSERTS NO RECORD, AND THAT IS A CORRECTNESS REQUIREMENT RATHER THAN A STYLE
 * PREFERENCE (D12, K2). The branch that lands here is *"you need an administrator's approval"*, and
 * the server refuses the write once 200 requests are already pending — at which point it redirects
 * to this same path having stored nothing. A sentence like *"we've recorded your request"* would
 * therefore be false on that branch, with no way for this page to tell. So the page states what is
 * true on every branch that reaches it: approval is needed, and sign-in works once it is granted.
 *
 * ⛔ THE EXPECTED DOMAIN IS NEVER NAMED, and neither is the reason. The person who is one approval
 * away and the person whose address was refused by policy both land on a page that discloses
 * nothing, because the surface is served to unauthenticated callers on an internet-facing host.
 * The operator who needs the distinction has it on the `oauth_login_failed` audit row's `reason`.
 *
 * ⛔ NO QUERY PARAMETER IS READ. A `?reason=` would be caller-chosen text rendered by a page we
 * serve, which deletes the property that makes the server's redirect table safe — every refusal
 * URL is `SITE_ADMIN_URL` plus a path literal the server owns, with no caller input in it. Same
 * rule as the two sibling refusal pages, and it is the rule an `AccessRequestView` field would
 * break if this page ever grew a read of its own.
 *
 * ⛔ AND NO RETRY. Retrying cannot help: an administrator has to act, and this page cannot tell the
 * person how long that takes. The one link goes back to `/login` so a different account can be
 * used, which is `AuthCardFrame`'s stated rule for a terminal card — never invent an action that
 * does nothing.
 */
import { AuthCardFrame } from '../shell/AuthCardFrame';
import { ButtonLink } from '../shell/ButtonLink';

export function AccessRequestedPage(): React.JSX.Element {
  return (
    <AuthCardFrame
      title="Access requested"
      /*
       * ⛔ FOCUS MOVES TO THE TITLE ON ARRIVAL (D-1), same rule as the two sibling refusal pages:
       * this card replaces the whole page after a redirect the person did not initiate.
       */
      autoFocusTitle
      notice={{
        /*
         * ⛔ `none`, the NEUTRAL verdict — not `bad` and not `good`. Nothing has failed and nothing
         * has succeeded: the person is being told what has to happen next. `bad` would announce a
         * routine policy outcome as an alarm through `NOTICE_ROLE`, and `good` would claim an
         * outcome this page cannot know, because the cap branch reaches it having written nothing.
         */
        verdict: 'none',
        message:
          'Signing in to the Zion admin console needs an administrator’s approval. You’ll be able to sign in once it’s granted.',
      }}
      testId="access-requested"
    >
      {/* A real anchor, not a `<button onClick={navigate}>`: `/login` has a URL, so right-click,
          middle-click and copy-link all keep working, and it announces as a link.

          ⛔ `default` tone, NOT `primary`, matching `/sign-in-unavailable` and differing from
          `/sign-in-incomplete` on purpose. A primary button recommends the action it carries, and
          this page recommends nothing — signing in again with the same account lands here again
          until an administrator has acted. */}
      <ButtonLink to="/login" size="md" data-testid="access-requested-back">
        Back to sign in
      </ButtonLink>
    </AuthCardFrame>
  );
}
